import { useState } from 'react'

import type { Operation } from '@fit-editor/fit-core'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrentActivity } from '@/hooks/use-current-activity'
import { useOperationPreview } from '@/hooks/use-operation-preview'
import { dateToLocalInputValue, localInputValueToDate } from '@/lib/time-input'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

export interface MetadataDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Edits the activity's file-identification metadata (M7). Scoped to
 * exactly what `applyOperation`'s `editMetadata` case actually patches --
 * `Activity.fileId`'s manufacturer/product/serialNumber/timeCreated --
 * and nothing else. Sport, activity name, and anything else a user might
 * expect under "edit metadata" are **not** wired here because `fit-core`
 * doesn't support patching them yet (confirmed by reading
 * `edit/operations.ts`'s `applyEditMetadata`); this milestone's handoff
 * report flags that as a scope gap worth a product decision, rather than
 * inventing fields in this dialog that silently no-op against the engine.
 *
 * Manufacturer is free text because that's the literal shape
 * `fileId.manufacturer` already has (a profile-mapped name string, not a
 * raw numeric id) -- but an unrecognized name will be caught by the
 * encoder (surfaced as a self-validation failure in the Export dialog,
 * never a silent corruption) rather than validated here against the full
 * FIT manufacturer enum, which would require duplicating
 * `@garmin/fitsdk`'s profile tables in the UI layer.
 */
export function MetadataDialog({ open, onOpenChange }: MetadataDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)

  // Only fetches while actually open -- see `useCurrentActivity`'s doc
  // comment for why (this dialog, like every edit dialog, stays mounted
  // while closed). Replaces a direct, main-thread-blocking `currentActivity`
  // call that -- unlike every other field in this component -- wasn't even
  // behind a `useMemo`, so it used to re-run the full edit-log replay on
  // every single keystroke in any of this dialog's four text fields.
  const { result: currentResult, loading: currentLoading } = useCurrentActivity(
    open ? loaded?.history : undefined,
  )
  const currentFileId = currentResult?.activity.fileId

  const [manufacturer, setManufacturer] = useState('')
  const [product, setProduct] = useState('')
  const [serialNumber, setSerialNumber] = useState('')
  const [timeCreated, setTimeCreated] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // The prefilled snapshot each field started from, captured once per
  // open transition below -- `hasChanges` compares against *this*, not
  // against emptiness, so a field that already had a value (the common
  // case: most activities already have a manufacturer/serial number)
  // doesn't make the dialog look like it has a pending change until the
  // user actually edits something.
  const [initial, setInitial] = useState({
    manufacturer: '',
    product: '',
    serialNumber: '',
    timeCreated: '',
  })

  // Prefill from the current fileId exactly once per "dialog is open AND
  // its data has actually arrived" transition -- same render-time-
  // adjustment pattern as `RangeOperationDialog`'s prefill (see its doc
  // comment for why this isn't a `useEffect`), extended with the
  // `currentResult != null` half of the key so a dialog opened before its
  // first `currentActivityAsync` fetch resolves still gets prefilled once
  // that fetch actually completes, rather than being stuck with an
  // all-empty snapshot taken before any data existed.
  const [prevPrefillReady, setPrevPrefillReady] = useState(false)
  const prefillReady = open && currentResult != null
  if (prefillReady !== prevPrefillReady) {
    setPrevPrefillReady(prefillReady)
    if (prefillReady) {
      const snapshot = {
        manufacturer: currentFileId?.manufacturer != null ? String(currentFileId.manufacturer) : '',
        product: currentFileId?.product != null ? String(currentFileId.product) : '',
        serialNumber: currentFileId?.serialNumber != null ? String(currentFileId.serialNumber) : '',
        timeCreated: dateToLocalInputValue(currentFileId?.timeCreated),
      }
      setManufacturer(snapshot.manufacturer)
      setProduct(snapshot.product)
      setSerialNumber(snapshot.serialNumber)
      setTimeCreated(snapshot.timeCreated)
      setInitial(snapshot)
    }
  }

  const parsedProduct = product.trim() === '' ? null : Number(product)
  const parsedSerialNumber = serialNumber.trim() === '' ? null : Number(serialNumber)
  const parsedTimeCreated = localInputValueToDate(timeCreated)
  const productValid = parsedProduct == null || Number.isFinite(parsedProduct)
  const serialValid = parsedSerialNumber == null || Number.isFinite(parsedSerialNumber)
  const valid = productValid && serialValid

  const patch: Record<string, unknown> = {}
  if (manufacturer.trim() !== '') patch.manufacturer = manufacturer.trim()
  if (parsedProduct != null && Number.isFinite(parsedProduct)) patch.product = parsedProduct
  if (parsedSerialNumber != null && Number.isFinite(parsedSerialNumber)) {
    patch.serialNumber = parsedSerialNumber
  }
  if (parsedTimeCreated != null) patch.timeCreated = parsedTimeCreated

  const hasChanges =
    manufacturer !== initial.manufacturer ||
    product !== initial.product ||
    serialNumber !== initial.serialNumber ||
    timeCreated !== initial.timeCreated
  const operation: Operation | null =
    valid && hasChanges && currentFileId != null ? { type: 'editMetadata', patch } : null

  // Called unconditionally (before either early return below) per React's
  // rules of hooks -- every dialog in this codebase that's always mounted
  // (see `EditMenu`'s doc comment) must call its hooks the same way on
  // every render regardless of whether an activity/fileId happens to be
  // selected yet.
  const preview = useOperationPreview(open ? loaded?.history : undefined, operation)
  const previewFileId = preview.preview?.activity.fileId

  if (loaded == null) return null
  if (currentLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby="metadata-loading-description">
          <DialogTitle>Edit metadata</DialogTitle>
          <DialogDescription id="metadata-loading-description">Loading…</DialogDescription>
        </DialogContent>
      </Dialog>
    )
  }
  if (currentFileId == null) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby="metadata-no-fileid-description">
          <DialogTitle>Edit metadata</DialogTitle>
          <DialogDescription id="metadata-no-fileid-description">
            This activity has no file-identification message to edit.
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const handleConfirm = async () => {
    if (operation == null) return
    setSubmitting(true)
    try {
      // `null` means the apply failed (the store already surfaced a
      // toast) -- keep the dialog open with the user's edits intact
      // rather than closing it as if the edit had actually been applied.
      const warnings = await applyOperationToSelected(operation)
      if (warnings != null) onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="metadata-description">
        <DialogTitle>Edit metadata</DialogTitle>
        <DialogDescription id="metadata-description">
          Edits this activity&apos;s file-identification fields. Leave a field unchanged to keep its
          current value.
        </DialogDescription>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="metadata-manufacturer" className="text-foreground text-xs font-medium">
              Manufacturer
            </label>
            <input
              id="metadata-manufacturer"
              type="text"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              placeholder="e.g. garmin"
              className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="metadata-product" className="text-foreground text-xs font-medium">
                Product
              </label>
              <input
                id="metadata-product"
                type="number"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="metadata-serial" className="text-foreground text-xs font-medium">
                Serial number
              </label>
              <input
                id="metadata-serial"
                type="number"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="metadata-time-created" className="text-foreground text-xs font-medium">
              Time created
            </label>
            <input
              id="metadata-time-created"
              type="datetime-local"
              step={1}
              value={timeCreated}
              onChange={(e) => setTimeCreated(e.target.value)}
              className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
            />
          </div>

          {!valid && (
            <p className="text-destructive text-xs">Product and serial number must be numbers.</p>
          )}
          {!hasChanges && valid && (
            <p className="text-muted-foreground text-xs">Change at least one field to apply.</p>
          )}
          {operation != null && previewFileId != null && (
            <div className="border-border bg-background flex flex-col gap-1 rounded-md border p-2 text-xs">
              <p className="text-foreground font-medium">Preview</p>
              <p className="text-muted-foreground">
                Manufacturer: {String(currentFileId.manufacturer ?? 'Unknown')} &rarr;{' '}
                {String(previewFileId.manufacturer ?? 'Unknown')}
              </p>
              <p className="text-muted-foreground">
                Product: {String(currentFileId.product ?? 'Unknown')} &rarr;{' '}
                {String(previewFileId.product ?? 'Unknown')}
              </p>
              <p className="text-muted-foreground">
                Serial number: {String(currentFileId.serialNumber ?? 'Unknown')} &rarr;{' '}
                {String(previewFileId.serialNumber ?? 'Unknown')}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={operation == null || submitting}
            onClick={() => void handleConfirm()}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
